<section class="ac">

  <header class="ac__header">
    <div>
      <h1 class="ac__title">
        <span class="ac__title-icon"><i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i></span>
        {{ 'ACTIVITY.TITLE' | translate }}
      </h1>
      <p class="ac__subtitle">{{ 'ACTIVITY.SUBTITLE' | translate }}</p>
    </div>

    @if (!loading() && !errored()) {
      <span class="ac__total">{{ 'ACTIVITY.COUNT' | translate: { count: total() } }}</span>
    }
  </header>

  <!-- Deux lectures de la même matière : les actes, et la fréquentation. -->
  <div class="ac__tabs" role="tablist">
    <button type="button" class="ac__tab" role="tab"
            [class.is-active]="tab() === 'journal'"
            [attr.aria-selected]="tab() === 'journal'"
            (click)="selectTab('journal')">
      <i class="fa-solid fa-list-check" aria-hidden="true"></i>
      {{ 'ACTIVITY.TAB_JOURNAL' | translate }}
    </button>
    <!-- La fréquentation (connexions de tous) exige `activity.read.all` :
         « mon journal » ne l'a pas, l'onglet n'y est pas. -->
    @if (!ownScope) {
      <button type="button" class="ac__tab" role="tab"
              [class.is-active]="tab() === 'connections'"
              [attr.aria-selected]="tab() === 'connections'"
              (click)="selectTab('connections')">
        <i class="fa-solid fa-right-to-bracket" aria-hidden="true"></i>
        {{ 'ACTIVITY.TAB_CONNECTIONS' | translate }}
      </button>
    }
  </div>

  <!-- Facettes : « tout » par défaut partout. -->
  <section class="ac__filters" role="search">
    <header class="ac__filters-head">
      <h2 class="ac__filters-title">
        <i class="fa-solid fa-filter" aria-hidden="true"></i>
        {{ 'QUESTION_BANK.FILTERS.TITLE' | translate }}
        @if (hasActiveFilters()) {
          <span class="ac__filters-count">
            {{ 'QUESTION_BANK.FILTERS.ACTIVE_COUNT' | translate: { count: activeCount() } }}
          </span>
        }
      </h2>
      @if (hasActiveFilters()) {
        <button type="button" class="ac__reset" (click)="resetFilters()">
          <i class="fa-solid fa-rotate-left" aria-hidden="true"></i>
          {{ 'QUESTION_BANK.FILTERS.RESET' | translate }}
        </button>
      }
    </header>

    <div class="ac__search">
      <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      <input type="search"
             [value]="textDraft()"
             (input)="onText($any($event.target).value)"
             [attr.aria-label]="'ACTIVITY.SEARCH' | translate"
             [placeholder]="'ACTIVITY.SEARCH_PLACEHOLDER' | translate">
    </div>

    <div class="ac__grid">
      <label class="ac__field">
        <span class="ac__label">{{ 'ACTIVITY.PERIOD' | translate }}</span>
        <select [value]="windowDays()" (change)="onWindow($any($event.target).value)">
          @for (w of windows; track w) {
            <option [value]="w">
              {{ (w ? 'ACTIVITY.WINDOW_DAYS' : 'ACTIVITY.WINDOW_ALL') | translate: { days: w } }}
            </option>
          }
        </select>
      </label>

      <label class="ac__field">
        <span class="ac__label">{{ 'ACTIVITY.ROLE_LABEL' | translate }}</span>
        <select [value]="role() ?? ''" (change)="onRole($any($event.target).value)">
          <option value="">{{ 'ACTIVITY.ALL_ROLES' | translate }}</option>
          @for (r of roles; track r) {
            <option [value]="r">{{ roleKey(r) | translate }}</option>
          }
        </select>
      </label>

      @if (tab() === 'journal') {
        <label class="ac__field">
          <span class="ac__label">{{ 'ACTIVITY.ACTOR' | translate }}</span>
          <select [value]="actorUserId() ?? ''"
                  [disabled]="actors().length === 0"
                  (change)="onActor($any($event.target).value)">
            <option value="">{{ 'ACTIVITY.ALL_ACTORS' | translate }}</option>
            @for (a of actors(); track a.userId) {
              @if (a.userId) {
                <option [value]="a.userId">{{ a.name || a.userId }}</option>
              }
            }
          </select>
        </label>

        <label class="ac__field">
          <span class="ac__label">{{ 'ACTIVITY.ACTION' | translate }}</span>
          <select [value]="action() ?? ''"
                  [disabled]="actions().length === 0"
                  (change)="onAction($any($event.target).value)">
            <option value="">{{ 'ACTIVITY.ALL_ACTIONS' | translate }}</option>
            @for (a of actions(); track a) {
              <option [value]="a">{{ a }}</option>
            }
          </select>
        </label>

        <label class="ac__toggle">
          <input type="checkbox"
                 [checked]="failuresOnly()"
                 (change)="onFailuresOnly($any($event.target).checked)">
          <span>{{ 'ACTIVITY.FAILURES_ONLY' | translate }}</span>
        </label>
      }
    </div>
  </section>

  @if (errored()) {
    <app-error-state
      titleKey="ACTIVITY.ERROR_TITLE"
      messageKey="ACTIVITY.ERROR_MESSAGE"
      [detail]="errorDetail()"
      (retry)="retry()">
    </app-error-state>
  }

  <!-- ══ Fréquentation : la synthèse avant le détail ══════════ -->
  @if (!errored() && tab() === 'connections' && metrics(); as m) {
    <div class="ac__metrics">
      @for (r of m.byRole; track r.role) {
        <article class="ac__metric">
          <h3 class="ac__metric-role">{{ roleKey(r.role) | translate }}</h3>
          <p class="ac__metric-value">{{ r.distinctUsers }}</p>
          <p class="ac__metric-caption">
            {{ 'ACTIVITY.METRIC_USERS' | translate }}
          </p>
          <p class="ac__metric-detail">
            {{ 'ACTIVITY.METRIC_SESSIONS' | translate: { count: r.sessionCount } }}
            · {{ 'ACTIVITY.METRIC_AVERAGE' | translate: { minutes: r.averageDurationMinutes } }}
          </p>
        </article>
      }

      <!-- Ce que la courbe ne montre pas : ceux qui ne sont pas venus. -->
      @for (d of m.dormant; track d.role) {
        @if (d.distinctUsers > 0) {
          <article class="ac__metric ac__metric--dormant">
            <h3 class="ac__metric-role">{{ roleKey(d.role) | translate }}</h3>
            <p class="ac__metric-value">{{ d.distinctUsers }}</p>
            <p class="ac__metric-caption">{{ 'ACTIVITY.METRIC_DORMANT' | translate }}</p>
            <p class="ac__metric-detail">{{ 'ACTIVITY.METRIC_DORMANT_HINT' | translate }}</p>
          </article>
        }
      }
    </div>

    @if (m.byDay.length > 0) {
      <section class="ac__chart">
        <h3 class="ac__chart-title">{{ 'ACTIVITY.CHART_TITLE' | translate }}</h3>
        <ol class="ac__bars">
          @for (d of m.byDay; track d.day) {
            <li class="ac__bar-item">
              <span class="ac__bar"
                    [style.block-size.%]="(d.sessionCount / peakDay()) * 100"
                    [attr.title]="(d.day | date: 'dd/MM') + ' — ' + d.sessionCount"></span>
              <span class="ac__bar-label">{{ d.day | date: 'dd/MM' }}</span>
            </li>
          }
        </ol>
      </section>
    }
  }

  @if (!errored()) {
    <div class="ac__table-wrap">

      <!-- ══ Journal ═══════════════════════════════════════════ -->
      @if (tab() === 'journal') {
        <table class="ac__table">
          <thead>
            <tr>
              <th class="ac__th">{{ 'ACTIVITY.COL_WHEN' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_WHO' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.ROLE_LABEL' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_WHAT' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_TARGET' | translate }}</th>
              <th class="ac__th ac__th--num">{{ 'ACTIVITY.COL_RESULT' | translate }}</th>
              <th class="ac__th ac__th--num">{{ 'ACTIVITY.COL_DURATION' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              @for (row of skeletonRows; track row) {
                <tr>
                  @for (cell of [0,1,2,3,4,5,6]; track cell) {
                    <td class="ac__skel-cell"><span class="ac__skel-bar"></span></td>
                  }
                </tr>
              }
            } @else {
              @for (e of entries(); track e.id) {
                <tr class="ac__row" [class.is-failed]="!e.succeeded">
                  <td class="ac__td ac__td--when">{{ e.occurredAt | date: 'dd/MM/yyyy HH:mm:ss' }}</td>
                  <td class="ac__td ac__td--who">{{ e.actorName || '—' }}</td>
                  <td class="ac__td">
                    <span class="ac__role" [attr.data-role]="e.actorRole">
                      {{ roleKey(e.actorRole) | translate }}
                    </span>
                  </td>
                  <td class="ac__td ac__td--what">
                    <!-- Un acte nommé se traduit ; un repli technique se lit tel
                         quel plutôt que d'afficher une clé i18n manquante. -->
                    <code class="ac__action">{{ e.action }}</code>
                    @if (e.httpMethod) {
                      <span class="ac__method">{{ e.httpMethod }}</span>
                    }
                  </td>
                  <td class="ac__td" [attr.title]="e.route">{{ targetLabel(e) }}</td>
                  <td class="ac__td ac__td--num">
                    <span class="ac__status" [class.is-failed]="!e.succeeded">{{ e.statusCode }}</span>
                  </td>
                  <td class="ac__td ac__td--num">{{ e.durationMs | number: '1.0-0' }} ms</td>
                </tr>
              }
            }
          </tbody>
        </table>
      }

      <!-- ══ Connexions ════════════════════════════════════════ -->
      @if (tab() === 'connections') {
        <table class="ac__table">
          <thead>
            <tr>
              <th class="ac__th">{{ 'ACTIVITY.COL_WHO' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.ROLE_LABEL' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_STARTED' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_LAST_SEEN' | translate }}</th>
              <th class="ac__th ac__th--num">{{ 'ACTIVITY.COL_DURATION_MIN' | translate }}</th>
              <th class="ac__th ac__th--num">{{ 'ACTIVITY.COL_REQUESTS' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_IP' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              @for (row of skeletonRows; track row) {
                <tr>
                  @for (cell of [0,1,2,3,4,5,6]; track cell) {
                    <td class="ac__skel-cell"><span class="ac__skel-bar"></span></td>
                  }
                </tr>
              }
            } @else {
              @for (s of sessions(); track s.id) {
                <tr class="ac__row">
                  <td class="ac__td ac__td--who">{{ s.userName || '—' }}</td>
                  <td class="ac__td">
                    <span class="ac__role" [attr.data-role]="s.role">
                      {{ roleKey(s.role) | translate }}
                    </span>
                  </td>
                  <td class="ac__td ac__td--when">{{ s.startedAt | date: 'dd/MM/yyyy HH:mm' }}</td>
                  <td class="ac__td ac__td--when">{{ s.lastSeenAt | date: 'dd/MM/yyyy HH:mm' }}</td>
                  <td class="ac__td ac__td--num">{{ s.durationMinutes }}</td>
                  <td class="ac__td ac__td--num">{{ s.requestCount }}</td>
                  <td class="ac__td" [attr.title]="s.userAgent">{{ s.ipAddress || '—' }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
      }
    </div>

    @if (isEmpty()) {
      <app-empty-state
        icon="clock-rotate-left"
        [titleKey]="hasActiveFilters() ? 'ACTIVITY.EMPTY_FILTERED_TITLE' : 'ACTIVITY.EMPTY_TITLE'"
        [messageKey]="hasActiveFilters() ? 'ACTIVITY.EMPTY_FILTERED_MESSAGE' : 'ACTIVITY.EMPTY_MESSAGE'">
      </app-empty-state>
    }

    @if (totalPages() > 1) {
      <nav class="ac__pagination" [attr.aria-label]="'ACTIVITY.PAGINATION' | translate">
        <button type="button" class="ac__page-btn" [disabled]="!canPrevious()" (click)="previousPage()">
          <i class="fa-solid fa-chevron-left ac__chev" aria-hidden="true"></i>
          {{ 'QUESTION_BANK.PAGINATION.PREVIOUS' | translate }}
        </button>
        <span class="ac__page-of">
          {{ 'QUESTION_BANK.PAGINATION.PAGE_OF' | translate: { current: currentPage(), total: totalPages() } }}
        </span>
        <button type="button" class="ac__page-btn" [disabled]="!canNext()" (click)="nextPage()">
          {{ 'QUESTION_BANK.PAGINATION.NEXT' | translate }}
          <i class="fa-solid fa-chevron-right ac__chev" aria-hidden="true"></i>
        </button>
      </nav>
    }
  }

  <!-- ══ Qui ne vient plus ═════════════════════════════════════ -->
  @if (!errored() && tab() === 'connections' && userActivity().length > 0) {
    <section class="ac__by-user">
      <h3 class="ac__chart-title">{{ 'ACTIVITY.BY_USER_TITLE' | translate }}</h3>
      <p class="ac__subtitle">{{ 'ACTIVITY.BY_USER_HINT' | translate }}</p>

      <div class="ac__table-wrap">
        <table class="ac__table">
          <thead>
            <tr>
              <th class="ac__th">{{ 'ACTIVITY.COL_WHO' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.ROLE_LABEL' | translate }}</th>
              <th class="ac__th">{{ 'ACTIVITY.COL_LAST_SEEN' | translate }}</th>
              <th class="ac__th ac__th--num">{{ 'ACTIVITY.COL_SESSIONS' | translate }}</th>
              <th class="ac__th ac__th--num">{{ 'ACTIVITY.COL_ACTIONS' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (u of pagedUserActivity(); track u.userId) {
              <tr class="ac__row">
                <td class="ac__td ac__td--who">{{ u.userName || '—' }}</td>
                <td class="ac__td">
                  <span class="ac__role" [attr.data-role]="u.role">
                    {{ roleKey(u.role) | translate }}
                  </span>
                </td>
                <td class="ac__td ac__td--when">
                  {{ u.lastSeenAt ? (u.lastSeenAt | date: 'dd/MM/yyyy HH:mm') : '—' }}
                </td>
                <td class="ac__td ac__td--num">{{ u.sessionCount }}</td>
                <td class="ac__td ac__td--num">{{ u.actionCount }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (userTotalPages() > 1) {
        <nav class="ac__pagination" [attr.aria-label]="'ACTIVITY.BY_USER_TITLE' | translate">
          <button type="button" class="ac__page-btn"
                  [disabled]="userCurrentPage() <= 1" (click)="previousUserPage()">
            <i class="fa-solid fa-chevron-left ac__chev" aria-hidden="true"></i>
            {{ 'QUESTION_BANK.PAGINATION.PREVIOUS' | translate }}
          </button>
          <span class="ac__page-of">
            {{ 'QUESTION_BANK.PAGINATION.PAGE_OF' | translate: { current: userCurrentPage(), total: userTotalPages() } }}
          </span>
          <button type="button" class="ac__page-btn"
                  [disabled]="userCurrentPage() >= userTotalPages()" (click)="nextUserPage()">
            {{ 'QUESTION_BANK.PAGINATION.NEXT' | translate }}
            <i class="fa-solid fa-chevron-right ac__chev" aria-hidden="true"></i>
          </button>
        </nav>
      }
    </section>
  }

</section>
